/**
 * FoodPulse — Install the App
 *
 * Full marketing-quality landing page for installing the FoodPulse PWA.
 * - QR code (via qrcode.react) to scan on mobile
 * - Phone mockup showing the app interface
 * - Platform-aware install CTA (Android one-tap / iOS Safari / Desktop)
 * - Matches the FoodPulse "Discover. Save. Share. Eat Better." visual language
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Download, Share2, Smartphone, CheckCircle2, RefreshCw,
  Leaf, ShoppingCart, MapPin, Heart, Globe, Recycle, Users,
  ChevronLeft,
} from "lucide-react";
import { applyServiceWorkerUpdate } from "@/lib/registerServiceWorker";

const GREEN  = "#1A5C38";
const ORANGE = "#E8611A";
const CREAM  = "#FAFAF5";

const SPLINK_SHORT_URL = "https://splink.pro/food-app";
const SPLINK_QR_PNG   = "https://splink.pro/api/qr/food-app?fmt=png";

const APP_URL = SPLINK_SHORT_URL;

const APP_CATEGORIES = [
  { icon: "🍎", label: "Food" },
  { icon: "🏷️", label: "Deals" },
  { icon: "📍", label: "Nearby" },
  { icon: "🌾", label: "Farms" },
  { icon: "📱", label: "Scanner" },
  { icon: "👨‍🍳", label: "Recipes" },
  { icon: "📋", label: "My Lists" },
  { icon: "❤️", label: "Donate" },
  { icon: "🏪", label: "Stores" },
  { icon: "🍽️", label: "Restaurants" },
  { icon: "🏛️", label: "Food Banks" },
  { icon: "♻️", label: "Food Rescue" },
  { icon: "💪", label: "Nutrition" },
  { icon: "🛒", label: "Marketplace" },
  { icon: "🙋", label: "Volunteers" },
  { icon: "⋯", label: "More" },
];

const FEATURES = [
  { icon: Leaf,        label: "Fresh Food",         sub: "Healthier Lives" },
  { icon: Users,       label: "Stronger",           sub: "Communities" },
  { icon: Globe,       label: "A Cleaner",          sub: "Planet" },
  { icon: Recycle,     label: "Less Food Waste",    sub: "A Brighter Tomorrow" },
];

const BENEFITS = [
  { icon: "🌱", title: "Support Local Farms",          desc: "Connect directly with farmers near you" },
  { icon: "🤝", title: "Help Your Community",           desc: "Share surplus food & reduce waste together" },
  { icon: "♻️", title: "Reduce Food Waste",             desc: "Smart lists, deals, and food rescue missions" },
  { icon: "🌍", title: "Healthier People, Happier Planet", desc: "Better nutrition, less packaging, cleaner world" },
];

export default function InstallFoodPulseApp() {
  const [deferredPrompt, setDeferredPrompt]   = useState(null);
  const [installed, setInstalled]             = useState(false);
  const [updateReg, setUpdateReg]             = useState(null);
  const [platform, setPlatform]               = useState("desktop");
  const [installDone, setInstallDone]         = useState(false);

  useEffect(() => {
    if (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone) {
      setInstalled(true);
    }
    const ua = navigator.userAgent || "";
    if (/iPhone|iPad|iPod/.test(ua))  setPlatform("ios");
    else if (/Android/i.test(ua))     setPlatform("android");
    else                              setPlatform("desktop");

    const onBip      = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const onInstalled = () => { setInstalled(true); setDeferredPrompt(null); };
    const onUpdate   = (e) => setUpdateReg(e.detail);
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("sw:update-ready", onUpdate);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("sw:update-ready", onUpdate);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") { setInstalled(true); setInstallDone(true); }
    } catch { /* ignore */ }
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="install-app-page">

      {/* ── Top bar (matches FoodPulse nav palette) ── */}
      <div style={{ background: GREEN }} className="py-3 px-4">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <Link
            to="/food-pulse"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white"
            data-testid="install-back-btn"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to FoodPulse
          </Link>
          <span className="text-[11px] tracking-widest text-white/60 uppercase">
            MY SOCIAL PULSE™ · POWERED BY FOODPULSE.NET
          </span>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="mx-auto max-w-7xl px-4 pt-10 pb-0 lg:pt-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_420px]">

          {/* Left — copy + QR + CTA */}
          <div className="flex flex-col justify-center">
            {/* Logo lockup */}
            <div className="flex items-center gap-3">
              <img
                src="/foodpulse-logo-round.png"
                alt="FoodPulse"
                className="h-14 w-14 rounded-full ring-2 ring-emerald-200 shadow-md"
              />
              <div>
                <div className="text-2xl font-black tracking-tight" style={{ color: GREEN }}>
                  FoodPulse<span style={{ color: ORANGE }}>.net</span>
                  <sup className="text-xs text-slate-400 ml-0.5">™</sup>
                </div>
                <div className="text-xs tracking-[0.2em] text-slate-500 uppercase">Food · People · Planet</div>
              </div>
              <div className="ml-4 text-sm italic text-slate-500 hidden sm:block">
                A Waste Less Brighter Tomorrow
              </div>
            </div>

            {/* Headline */}
            <div className="mt-8">
              <p className="text-xs tracking-widest text-slate-500 uppercase mb-2">
                My Social Pulse™ · Powered by FoodPulse.net
              </p>
              <h1 className="text-5xl font-black leading-tight sm:text-6xl">
                <span style={{ color: GREEN }}>Discover.</span>{" "}
                <span style={{ color: ORANGE }}>Save.</span>
                <br />
                <span style={{ color: GREEN }}>Share.</span>{" "}
                <span style={{ color: ORANGE }}>Eat Better.</span>
              </h1>
              <p className="mt-4 text-lg text-slate-700 max-w-xl">
                Your complete food life — local deals, fresh food, recipes,
                nutrition, shopping lists and food rescue in one place.
              </p>
              <p className="mt-2 text-base text-slate-500 max-w-xl">
                Connecting farms, stores, restaurants, food banks and neighbors to
                build healthier communities while reducing food waste.
              </p>
            </div>

            {/* Feature pills */}
            <div className="mt-6 flex flex-wrap gap-3">
              {FEATURES.map(({ icon: Icon, label, sub }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-sm shadow-sm"
                >
                  <Icon className="h-4 w-4" style={{ color: GREEN }} />
                  <span className="font-semibold text-slate-800">{label}</span>
                  <span className="text-slate-500">{sub}</span>
                </div>
              ))}
            </div>

            {/* Install CTA block */}
            <div className="mt-8 flex flex-col sm:flex-row gap-6 items-start">
              {/* SpLink.pro QR code — branded, click-tracked */}
              <div className="flex flex-col items-center gap-2">
                <a href={SPLINK_SHORT_URL} target="_blank" rel="noopener noreferrer">
                  <div
                    className="rounded-2xl border-4 p-3 shadow-xl bg-white hover:shadow-2xl transition"
                    style={{ borderColor: GREEN }}
                    data-testid="install-app-qr-container"
                  >
                    <img
                      src={SPLINK_QR_PNG}
                      alt="Scan to install FoodPulse"
                      width={140}
                      height={140}
                      className="block rounded-lg"
                      data-testid="install-app-qr"
                    />
                  </div>
                </a>
                <a
                  href={SPLINK_SHORT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-mono font-bold hover:underline"
                  style={{ color: GREEN }}
                  data-testid="install-app-short-url"
                >
                  splink.pro/food-app
                </a>
                <p className="text-center text-xs text-slate-500 max-w-[160px]">
                  Scan with your phone camera to install
                </p>
              </div>

              {/* CTA */}
              <div className="flex flex-col gap-3 justify-center">
                {installed || installDone ? (
                  <div
                    className="flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow"
                    style={{ background: GREEN }}
                    data-testid="install-app-already"
                  >
                    <CheckCircle2 className="h-5 w-5" />
                    FoodPulse is installed!
                    {updateReg && (
                      <button
                        onClick={() => applyServiceWorkerUpdate(updateReg)}
                        className="ml-2 rounded-md bg-white/20 px-2 py-0.5 text-xs hover:bg-white/30"
                        data-testid="install-app-update"
                      >
                        <RefreshCw className="inline h-3 w-3 mr-1" />Update
                      </button>
                    )}
                  </div>
                ) : platform === "ios" ? (
                  <IosSteps />
                ) : deferredPrompt ? (
                  <button
                    onClick={install}
                    data-testid="install-app-primary-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 text-lg font-black text-white shadow-lg transition hover:opacity-90 active:scale-95"
                    style={{ background: GREEN }}
                  >
                    <Download className="h-5 w-5" />
                    Install App — Free
                  </button>
                ) : platform === "android" ? (
                  <AndroidFallback />
                ) : (
                  <DesktopFallback />
                )}

                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> No app store</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Auto-updates</span>
                  <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Works offline</span>
                </div>
              </div>
            </div>

            {/* Footer tagline */}
            <p className="mt-8 text-base italic font-medium" style={{ color: GREEN }}>
              Good Food Brings People Together
            </p>
          </div>

          {/* Right — phone mockup */}
          <div className="flex items-center justify-center">
            <PhoneMockup />
          </div>
        </div>
      </section>

      {/* ── Benefits strip ── */}
      <section className="mt-16 border-t border-emerald-100" style={{ background: "#F0F9F4" }}>
        <div className="mx-auto max-w-7xl px-4 py-12">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map(({ icon, title, desc }) => (
              <div key={title} className="flex flex-col items-start gap-2 rounded-2xl bg-white p-5 shadow-sm border border-emerald-100">
                <span className="text-3xl">{icon}</span>
                <div className="text-sm font-bold uppercase tracking-wide" style={{ color: GREEN }}>{title}</div>
                <p className="text-sm text-slate-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Share strip ── */}
      <section className="border-t border-emerald-100 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-4 py-8">
          <div>
            <div className="text-lg font-bold" style={{ color: GREEN }}>Share FoodPulse with a friend</div>
            <p className="mt-1 text-sm text-slate-500">Scan the QR code, copy the link, or share directly.</p>
            <div className="mt-3 flex items-center gap-2">
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-mono text-slate-600 select-all">
                {APP_URL}
              </span>
              <button
                onClick={() => navigator.share?.({ title: "FoodPulse", url: APP_URL })}
                className="rounded-full p-2 hover:bg-emerald-50 border border-emerald-200"
                title="Share"
              >
                <Share2 className="h-4 w-4 text-emerald-600" />
              </button>
            </div>
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="rounded-xl border-2 border-emerald-200 p-2 bg-white shadow">
              <img src={SPLINK_QR_PNG} alt="QR code" width={100} height={100} className="block rounded" />
            </div>
            <span className="text-[11px] text-slate-400">Scan to install</span>
          </div>
        </div>
      </section>

    </div>
  );
}

/* ── Phone Mockup ── */
function PhoneMockup() {
  return (
    <div className="relative" style={{ perspective: "1200px" }}>
      {/* Phone shell */}
      <div
        className="relative rounded-[3rem] shadow-2xl overflow-hidden border-[8px] border-slate-800"
        style={{ width: 300, background: "#fff", transform: "rotateY(-6deg) rotateX(2deg)" }}
        data-testid="install-phone-mockup"
      >
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10 w-24 h-6 bg-slate-800 rounded-b-2xl" />

        {/* Status bar */}
        <div className="flex items-center justify-between px-5 pt-7 pb-1" style={{ background: "#f8fdf9" }}>
          <span className="text-[10px] font-semibold text-slate-700">9:41</span>
          <div className="flex items-center gap-0.5">
            <span className="text-[10px] text-slate-500">▐▐▐</span>
            <span className="text-[10px] text-slate-500 ml-1">WiFi</span>
          </div>
        </div>

        {/* App header */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-100" style={{ background: "#f8fdf9" }}>
          <img src="/foodpulse-logo-round.png" alt="FoodPulse" className="h-6 w-6 rounded-full" />
          <div>
            <div className="text-[11px] font-bold text-emerald-800">FoodPulse.net</div>
            <div className="text-[9px] text-slate-400">Good Food. Brighter Tomorrows.</div>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <div className="h-6 w-6 rounded-full bg-slate-200" />
          </div>
        </div>

        {/* Search bar */}
        <div className="px-3 py-2" style={{ background: "#f8fdf9" }}>
          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5">
            <span className="text-[10px] text-slate-400">🔍 Search food, deals, farms, recipes…</span>
          </div>
        </div>

        {/* Categories grid */}
        <div className="px-3 pb-2 pt-1" style={{ background: "#fff" }}>
          <div className="grid grid-cols-4 gap-1">
            {APP_CATEGORIES.map(({ icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-0.5 py-2 rounded-xl hover:bg-emerald-50 cursor-default">
                <span className="text-xl leading-none">{icon}</span>
                <span className="text-[8.5px] text-slate-600 text-center leading-tight">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Today Near You */}
        <div className="px-3 py-2 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-slate-800">📍 Today Near You</span>
            <span className="text-[9px] text-emerald-600">See All →</span>
          </div>
          <div className="flex gap-2 overflow-hidden">
            {[
              { name: "Fresh Tomatoes", price: "$1.49/lb", was: "$2.99", off: "50%" },
              { name: "Bananas", price: "$0.69/lb", was: "$1.19", off: "30%" },
              { name: "Org. Lettuce", price: "$1.99 ea", was: "$3.49", off: "40%" },
            ].map((item) => (
              <div key={item.name} className="flex-shrink-0 w-[86px] rounded-xl overflow-hidden border border-slate-100">
                <div
                  className="h-12 flex items-center justify-center text-xs font-bold text-white"
                  style={{ background: "#1A5C38" }}
                >
                  <span className="rounded-full bg-orange-500 px-1.5 py-0.5 text-[8px] font-black">{item.off} OFF</span>
                </div>
                <div className="p-1">
                  <div className="text-[8px] font-semibold text-slate-700 truncate">{item.name}</div>
                  <div className="text-[9px] font-bold" style={{ color: "#E8611A" }}>{item.price}</div>
                  <div className="text-[7.5px] text-slate-400 line-through">{item.was}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Home indicator */}
        <div className="flex justify-center pb-2 pt-1">
          <div className="h-1 w-28 rounded-full bg-slate-300" />
        </div>
      </div>

      {/* Globe decoration */}
      <div
        className="absolute -right-6 top-4 flex h-24 w-24 items-center justify-center rounded-full border-4 border-emerald-100 bg-emerald-50 shadow-lg text-center text-[10px] font-bold text-emerald-800 leading-tight"
        style={{ transform: "rotateY(8deg)" }}
      >
        <div>
          <Globe className="mx-auto h-6 w-6 text-emerald-400" />
          Local Food<br />Global<br />Impact
        </div>
      </div>
    </div>
  );
}

/* ── Platform-specific helpers ── */
function IosSteps() {
  return (
    <ol className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm shadow" data-testid="install-app-ios-steps">
      <li className="flex items-start gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">1</span>
        <span><strong>Open in Safari</strong> — other iOS browsers can't install PWAs.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">2</span>
        <span>Tap <Share2 className="inline h-3.5 w-3.5 mx-0.5" /> <strong>Share</strong> at the bottom of the screen.</span>
      </li>
      <li className="flex items-start gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">3</span>
        <span>Tap <Download className="inline h-3.5 w-3.5 mx-0.5" /> <strong>Add to Home Screen</strong> — done!</span>
      </li>
    </ol>
  );
}

function AndroidFallback() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm shadow" data-testid="install-app-android-fallback">
      <Smartphone className="h-5 w-5 mb-1 text-emerald-600" />
      Tap the browser menu <strong>(⋮)</strong> → <strong>Install app</strong> or{" "}
      <strong>Add to Home screen</strong>. Works with Chrome, Edge and Samsung Internet.
    </div>
  );
}

function DesktopFallback() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm shadow" data-testid="install-app-desktop-fallback">
      <div className="font-semibold mb-1">Install from your desktop browser</div>
      Look for the <strong>install icon</strong> <span className="font-mono bg-slate-100 rounded px-1">⬇</span> at the right
      of the address bar in Chrome, Edge, Arc or Brave — or scan the QR code with your phone.
    </div>
  );
}
